import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Query param used to tell `/login` that the previous attempt timed out on the picker,
 * so it can explain why the user is back there instead of silently resetting them.
 */
export const SESSION_EXPIRED_PARAM = 'sessionExpired';

/**
 * Admission to `/select-account`.
 *
 * This is the only route in the app reachable **both with and without a session**, which is
 * why it cannot use `authGuard`:
 *
 * - **Selection mode** — a multi-merchant user has authenticated but the backend issued no
 *   token until they choose. `authGuard` would bounce them straight back to `/login`, which
 *   is the deadlock the original implementation worked around by pretending the session was
 *   already complete.
 * - **Confirmation mode** — a single-merchant user has a full session and still sees the
 *   picker (decision D1), so a pending-only guard would reject them.
 *
 * Order matters: the expiry check runs before the session check so a timed-out pending
 * login is reported as such rather than silently redirected.
 */
export const accountSelectionGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Timed out on the picker — say so rather than resetting them without explanation.
  if (authService.hasExpiredPendingLogin()) {
    authService.clearPendingLogin();
    return router.createUrlTree(['/login'], {
      queryParams: { [SESSION_EXPIRED_PARAM]: 1 },
    });
  }

  // Selection mode: credential parked, awaiting a choice.
  if (authService.hasFreshPendingLogin()) {
    return true;
  }

  // Confirmation mode: session already established.
  if (authService.isAuthenticated()) {
    return true;
  }

  return router.createUrlTree(['/login']);
};
